// WorkspaceTerminal — xterm.js bound to the tmux session running inside a
// remote env-host workspace.
//
// Wire contract, owned by `lib/operation_web/channels/terminal_channel.ex`:
//
//   join            → reply `{ backlog_b64 }` (base64: a join reply is JSON,
//                     and PTY scrollback is not valid UTF-8 in general)
//   "out"    server → an ArrayBuffer of raw PTY bytes (a binary frame, which
//                     is why it can carry a split multi-byte character)
//   "in"     client → `{ b: <string> }`
//   "resize" client → `{ cols, rows }`
//
// The terminal is a *view* of the PTY, never its owner: every byte the user
// types goes to the host and comes back through "out". Nothing is echoed
// locally, so a reload redraws from the server's ring buffer and tmux's own
// redraw rather than from anything this module kept.

import { Terminal } from "@xterm/xterm";
import type { ITheme } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { WebglAddon } from "@xterm/addon-webgl";
import { Socket } from "phoenix";
import type { Channel } from "phoenix";
import { acquireLspSocket, releaseLspSocket } from "../lsp-hook-connection";
import { readUserSocketToken } from "../user-socket-token";
import { cssVar, cssVarRaw } from "./ci-pipeline/theme";
import type { LiveViewHook, EditorPushEventWire } from "./types";
import {
  illuminateCssClassCodeBrowser,
  validEditorElementId,
} from "../../../.dox/ui";
import { isValidTerminalDimension } from "../../../.dox/ci";

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

// Every `--c-*` var holds a bare `R G B` triple so `rgb(var(--x) / 0.5)`
// works in CSS. xterm parses colors itself and its parser predates the
// space-separated syntax, so hand it the comma form.
export const tripleToCss = (triple: string): string =>
  `rgb(${triple.trim().split(/\s+/).join(", ")})`;

// Reads the 16 ANSI slots plus the chrome colors out of the live cascade.
// Deliberately takes the reader as an argument: `colors()` in
// ./ci-pipeline/theme.ts memoises, and `ThemeApply` flips `data-theme` on
// <html> at runtime, so a cached palette would freeze the terminal at
// whatever theme happened to be active when the first canvas hook mounted.
export const themeToXterm = (read: (name: string) => string): ITheme => {
  const c = (name: string): string => tripleToCss(read(name));
  return {
    background: c("--c-bg"),
    foreground: c("--c-text"),
    cursor: c("--c-accent"),
    cursorAccent: c("--c-bg"),
    selectionBackground: c("--c-bg-active"),
    black: c("--c-ansi-black"),
    red: c("--c-ansi-red"),
    green: c("--c-ansi-green"),
    yellow: c("--c-ansi-yellow"),
    blue: c("--c-ansi-blue"),
    magenta: c("--c-ansi-magenta"),
    cyan: c("--c-ansi-cyan"),
    white: c("--c-ansi-white"),
    brightBlack: c("--c-ansi-black-bright"),
    brightRed: c("--c-ansi-red-bright"),
    brightGreen: c("--c-ansi-green-bright"),
    brightYellow: c("--c-ansi-yellow-bright"),
    brightBlue: c("--c-ansi-blue-bright"),
    brightMagenta: c("--c-ansi-magenta-bright"),
    brightCyan: c("--c-ansi-cyan-bright"),
    brightWhite: c("--c-ansi-white-bright"),
  };
};

// Typography, same shape as the colours: read out of the live cascade
// rather than restated.
//
// xterm resolves neither `var()` nor a CSS length. Its WebGL and canvas
// renderers assemble a `ctx.font` shorthand, and the 2D context's parser
// rejects an unparseable value outright — leaving the canvas default, a
// proportional sans-serif at 10px. So `fontFamily: "var(--font-mono)"`
// rendered the panel in the one face the rest of the app never uses, and
// `fontSize: 13` restated `--text-min` where a theme is free to change it.
//
// The chain mirrors the app-wide rule in assets/css/app.css: the selected
// mono face, then the nerd face so a prompt's glyphs are not .notdef boxes,
// then the generic.
export const terminalFontFamily = (read: (name: string) => string): string => {
  const mono = read("--font-mono") || '"DejaVu Mono"';
  return `${mono}, "Symbols Nerd Font", monospace`;
};

// `--code-font-size`, the SAME variable `#codeViewer .cm-scroller` reads.
//
// This terminal sits directly beside the code viewer in one panel, so
// anything they disagree about reads as two different editors bolted
// together. It used to take `--text-min` (13px) against the viewer's
// hardcoded 14px.
//
// The fallback is the viewer's value, not xterm's 15px, which is a size
// nothing else in this app uses.
export const terminalFontSize = (read: (name: string) => string): number => {
  const px = Number.parseFloat(read("--code-font-size"));
  return Number.isFinite(px) && px > 0 ? px : 14;
};

// `--code-line-height`, again the viewer's own variable.
//
// This is the one that was most visible: xterm's `lineHeight` defaults
// to 1.0 — rows exactly one em tall — while the viewer renders at 1.6.
// Two panes of the same monospace text at the same size with visibly
// different leading.
//
// xterm wants a unitless MULTIPLE of the font size, which is what CSS
// `line-height: 1.6` already is. A theme that ever writes a length here
// (`line-height: 22px`) would not be a multiplier, so guard the range
// rather than trust the parse: anything outside sane leading falls back.
export const terminalLineHeight = (read: (name: string) => string): number => {
  const value = Number.parseFloat(read("--code-line-height"));
  return Number.isFinite(value) && value >= 1 && value <= 3 ? value : 1.6;
};

// Convert a CSS `line-height` multiple into xterm's `lineHeight`.
//
// They are multiples of DIFFERENT things, which is the whole bug:
//
//   CSS    line-height: 1.6   → 1.6 × FONT-SIZE          = 22.4px at 14px
//   xterm  lineHeight: 1.6    → 1.6 × MEASURED CHAR HEIGHT
//
// xterm's own renderer does `Math.floor(char.height * lineHeight)`, and
// `char.height` is the font's natural line box — about 16.4px for
// DejaVu Mono at 14px, not 14. So handing xterm the CSS value directly
// gave rows ~17% taller than the CodeMirror pane beside it: visibly,
// slightly too high.
//
// Pure, so the arithmetic is testable without a browser; the one
// measurement it needs is passed in.
export const xtermLineHeight = (
  cssLineHeight: number,
  fontSize: number,
  charHeight: number,
): number => {
  if (!Number.isFinite(charHeight) || charHeight <= 0) return cssLineHeight;
  const ratio = (fontSize * cssLineHeight) / charHeight;
  // A measurement this far out is a broken font metric, not a taller
  // line: fall back rather than render a terminal nobody can read.
  return ratio >= 0.5 && ratio <= 3 ? ratio : cssLineHeight;
};

// The one DOM measurement: the height of a character box at this font,
// which is what xterm's own CharSizeService measures.
//
// `line-height: normal` is deliberate — that is the box xterm's
// `char.height` reports, so this has to ask the same question. Returns
// 0 where there is no layout (jsdom), and `xtermLineHeight` then leaves
// the CSS value alone.
export const measureCharHeight = (
  fontFamily: string,
  fontSize: number,
): number => {
  const probe = document.createElement("span");
  probe.style.cssText =
    `position:absolute;top:-9999px;left:-9999px;visibility:hidden;` +
    `white-space:pre;line-height:normal;font-size:${fontSize}px;` +
    `font-family:${fontFamily};`;
  probe.textContent = "W";
  document.body.appendChild(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return height;
};

export interface ResizePayload {
  readonly cols: number;
  readonly rows: number;
}

// `null` means "do not send". FitAddon reports 0 columns for a container
// with no layout box — a panel that is closed, or one measured before the
// first paint — and the range itself is `valid TerminalDimension` in
// domain/ci/env-host.dox, read here through the generated predicate rather
// than restated. The host drops an out-of-range frame anyway; not sending it
// saves a wire frame and a log line nobody reads.
export const fitToResize = (
  cols: number,
  rows: number,
): ResizePayload | null =>
  isValidTerminalDimension(cols) && isValidTerminalDimension(rows)
    ? { cols, rows }
    : null;

// A binary channel frame arrives as an ArrayBuffer. Anything else is a
// serializer change or a server bug; drop it rather than feed xterm garbage.
export const bytesFromPush = (payload: unknown): Uint8Array =>
  payload instanceof ArrayBuffer ? new Uint8Array(payload) : new Uint8Array(0);

// Whether `onData` is carrying the terminal's own answer to a query rather
// than something the user typed.
//
// xterm emits both through the same event, one sequence per call. Its
// answers are complete reports: CSI finals `c` (DA1/DA2), `n` and `R`
// (DSR, cursor position), `$y` (DECRQM) and `t` (window reports), and OSC
// or DCS strings closed by BEL or ST. No key produces any of these whole,
// with one exception: a MODIFIED F3 is `ESC [1;<m>R`, which a replay window
// drops as it would a cursor report. A bare `ESC ]` or `ESC P` is Alt+key
// and has no terminator, so it is not a report.
const TERMINAL_REPORT =
  /^(?:\x1b\[[?>=]?[\d;]*\$?[cnRyt]|\x1b[\]P][^\x07\x1b]*(?:\x07|\x1b\\))$/;

export const isTerminalReport = (data: string): boolean =>
  TERMINAL_REPORT.test(data);

export const base64ToBytes = (b64: string): Uint8Array => {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
};

// The class name is dox-owned (`CssClassCodeBrowser` in
// domain/ui/css-classes.dox) so HEEx, app.css and this file cannot drift.
const FULLSCREEN_CLASS = illuminateCssClassCodeBrowser(
  "workspaceTerminalFullscreen",
);

// Fullscreen is a class on <body>, which sits outside the LiveView patch
// root — morphdom cannot strip it, and the panel never leaves its templated
// position, so there is no reparenting to undo. Same mechanism as the CI
// build log's; see the rule in assets/css/app.css.
export const setWorkspaceFullscreen = (on: boolean): void => {
  document.body.classList.toggle(FULLSCREEN_CLASS, on);
};

export const isWorkspaceFullscreen = (): boolean =>
  document.body.classList.contains(FULLSCREEN_CLASS);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

type WorkspaceTerminalHook = LiveViewHook<EditorPushEventWire> & {
  _term: Terminal | null;
  _fit: FitAddon | null;
  _channel: Channel | null;
  _socket: Socket | null;
  _sessionId: string | null;
  _resizeObserver: ResizeObserver | null;
  // Pending `requestAnimationFrame` handle for a coalesced fit, or null.
  _fitFrame: number | null;
  // Trailing-debounce handle for the WIRE resize, and the geometry it
  // will send. Separate from `_fitFrame`: the local fit runs per frame,
  // the PTY hears once the drag settles.
  _resizeTimer: ReturnType<typeof setTimeout> | null;
  _pendingResize: ResizePayload | null;
  // True while the join backlog is being parsed. The terminal's ANSWERS
  // to questions inside the replay are dropped; what the user types in
  // that window is held in `_pendingInput` and sent once it ends.
  _replaying: boolean;
  _pendingInput: readonly string[];
  _themeObserver: MutationObserver | null;
  _keyHandler: ((e: KeyboardEvent) => void) | null;
  _fsHandler: (() => void) | null;
  _fsButton: HTMLElement | null;
  // The last geometry SEEN, which guards the observer storm, and the last
  // geometry PUSHED, which is what the PTY actually has. They differ
  // mid-drag: a size the drag passes through is seen but never sent.
  _lastGeometry: string;
  _lastSent: string;
};

// One socket is shared with the LSP channel through the refcount in
// ../lsp-hook-connection.ts — a second WebSocket to the same endpoint buys
// nothing. `Socket.connect()` mutates in place, so return it after.
const makeUserSocket = (): Socket => {
  const token = readUserSocketToken();
  const socket = new Socket("/ci/socket", { params: token ? { token } : {} });
  socket.connect();
  return socket;
};

// How long the geometry must hold still before the PTY is told.
//
// Exported so a test names the same number the code does.
export const RESIZE_SETTLE_MS = 120;

// Tell the PTY the new geometry, once the geometry stops moving.
//
// The local fit is immediate — xterm reflows as you drag, which is what
// makes the drag feel live. The WIRE resize is not: every distinct
// cols×rows that reaches the far end makes tmux redraw the whole pane,
// and a drag crosses a column boundary every few pixels. At 20-60 sizes
// a second that is 20-60 full-screen repaints a second arriving over a
// WebSocket, which is the flashing.
//
// This was invisible until the session actually HAD a controlling
// terminal: before that, tmux never heard a resize, so it never redrew
// and there was nothing to flash. Fixing the resize is what made the
// storm visible.
//
// A trailing debounce rather than an event, deliberately: there is no
// "the user stopped dragging" signal to wire to. A divider drag, a
// window resize and a fullscreen toggle all end differently and some
// emit nothing at all. This is rate-limiting a continuous stream, not a
// timer standing in for a completion callback — the distinction the
// events-over-timers rule is actually about.
//
// The dedupe stays in front of it: a drag that returns to the size the
// PTY already has must not schedule a redundant frame. "Already has" is
// the last size PUSHED, not the last one seen — a drag out and back
// within one window passes through sizes that were never sent, and
// comparing against those pushed the PTY's own size a second time.
const geometryKey = (payload: ResizePayload): string =>
  `${payload.cols}x${payload.rows}`;

const pushResize = (
  hook: WorkspaceTerminalHook,
  payload: ResizePayload,
): void => {
  const key = geometryKey(payload);
  if (key === hook._lastSent) return;
  hook._lastSent = key;
  hook._channel?.push("resize", { ...payload });
};

export const sendResize = (
  hook: WorkspaceTerminalHook,
  payload: ResizePayload,
): void => {
  const key = geometryKey(payload);
  if (key === hook._lastGeometry) return;
  hook._lastGeometry = key;

  // Idle: send at once and open the window. This keeps "report geometry
  // on join" instant — a workspace must not spend its first frames at
  // the wrong size — and a single deliberate resize is never delayed.
  if (hook._resizeTimer === null) {
    pushResize(hook, payload);
    hook._resizeTimer = setTimeout(() => {
      settle(hook);
    }, RESIZE_SETTLE_MS);
    return;
  }

  // Mid-drag: hold the newest size and RESTART the window. Nothing goes
  // out until the geometry stops moving.
  //
  // This is a debounce, not a throttle, and the difference is the whole
  // fix. A throttle — send the newest once per window, then re-arm —
  // looks right and measured 6 frames a SECOND against a real drag,
  // because a drag spans seconds and re-opens the window thirty times.
  // Six full tmux repaints a second is still flashing. Restarting the
  // window instead collapses a drag of any length to two frames: one
  // when it starts, one when it ends.
  hook._pendingResize = payload;
  clearTimeout(hook._resizeTimer);
  hook._resizeTimer = setTimeout(() => {
    settle(hook);
  }, RESIZE_SETTLE_MS);
};

// Trailing edge: the geometry has held still for a full window, so send
// the last one seen — unless the PTY already has it — and go idle.
// Deliberately does NOT re-arm — that re-arming is what turned this into
// a per-window throttle.
const settle = (hook: WorkspaceTerminalHook): void => {
  const pending = hook._pendingResize;
  hook._pendingResize = null;
  hook._resizeTimer = null;
  if (pending) pushResize(hook, pending);
};

// A face the user has just picked may not be downloaded yet
// (`font-display: swap` in fonts.css), and xterm measures its cell the
// instant `fontFamily` changes — with the fallback's metrics if the real
// face has not arrived, which it then keeps until something else forces a
// remeasure. Ask for the face first. `document.fonts.load` resolves
// immediately for one already in the cascade, which is the ordinary case:
// the app-wide `*` rule in app.css loads the mono face long before this
// panel opens. jsdom has no FontFaceSet, hence the guard.
const withFontLoaded = (spec: string, then: () => void): void => {
  const fonts = (document as Partial<Document>).fonts;
  if (!fonts) {
    then();
    return;
  }
  void fonts.load(spec).then(then, then);
};

// Theme and typography both live in the cascade and both change under the
// user's hand, so one function re-reads both. The font half is gated on an
// actual change: xterm drops a set to the value it already holds, and
// re-fitting on every theme flip would be churn for nothing.
const applyAppearance = (hook: WorkspaceTerminalHook): void => {
  const term = hook._term;
  if (!term) return;
  term.options.theme = themeToXterm(cssVar);

  const fontFamily = terminalFontFamily(cssVarRaw);
  const fontSize = terminalFontSize(cssVarRaw);
  const lineHeight = xtermLineHeight(
    terminalLineHeight(cssVarRaw),
    fontSize,
    measureCharHeight(fontFamily, fontSize),
  );
  if (
    term.options.fontFamily === fontFamily &&
    term.options.fontSize === fontSize &&
    term.options.lineHeight === lineHeight
  ) {
    return;
  }

  withFontLoaded(`${fontSize}px ${fontFamily}`, () => {
    if (hook._term !== term) return;
    term.options.fontFamily = fontFamily;
    term.options.fontSize = fontSize;
    term.options.lineHeight = lineHeight;
    // New cell metrics: the old cols/rows no longer fill the panel, and the
    // PTY has to hear about it.
    hook._fit?.fit();
  });
};

const attach = (hook: WorkspaceTerminalHook): void => {
  const sessionId = hook.el.dataset["sessionId"];
  // The panel renders before a workspace is picked; there is simply nothing
  // to attach to yet. `updated` runs this again once the id appears.
  if (!sessionId) return;

  const term = new Terminal({
    theme: themeToXterm(cssVar),
    fontFamily: terminalFontFamily(cssVarRaw),
    fontSize: terminalFontSize(cssVarRaw),
    // Same leading as `#codeViewer .cm-scroller`. xterm defaults to 1.0
    // — rows exactly one em tall — against the viewer's 1.6, which is
    // what made two panes of the same text at the same size look like
    // different editors.
    lineHeight: xtermLineHeight(
      terminalLineHeight(cssVarRaw),
      terminalFontSize(cssVarRaw),
      measureCharHeight(
        terminalFontFamily(cssVarRaw),
        terminalFontSize(cssVarRaw),
      ),
    ),
    cursorBlink: true,
    // tmux redraws the whole pane on reconnect and the server keeps its own
    // 256 KiB ring, so xterm's scrollback only needs to cover what the user
    // scrolls back through in the panel itself.
    scrollback: 5000,
    allowProposedApi: true,
  });
  const fit = new FitAddon();
  term.loadAddon(fit);
  term.open(hook.el);
  // WebGL must be loaded after open() — it needs the element's canvas. It
  // throws where WebGL2 is unavailable (software rendering, some VMs); xterm
  // then keeps its DOM renderer, which is slower but correct.
  try {
    term.loadAddon(new WebglAddon());
  } catch (err) {
    console.warn(
      "[WorkspaceTerminal] WebGL unavailable, using DOM renderer",
      err,
    );
  }

  hook._term = term;
  hook._fit = fit;
  hook._socket = acquireLspSocket(makeUserSocket);

  term.onData((data: string) => {
    // Drop terminal-generated output while the backlog replays.
    //
    // The backlog is recorded PTY OUTPUT, and a session's first bytes
    // include the queries tmux and `nix develop` send to identify the
    // terminal — DA1 (`ESC [ c`), DA2 (`ESC [ > c`) and friends.
    // Replaying them asks xterm the same questions again; xterm answers,
    // as it must, and the answer leaves as INPUT. The shell is sitting
    // at a prompt, so it echoes `1;2c0;276;0c` back at the user: they
    // opened an existing workspace and it typed at them.
    //
    // Gated on xterm's own write-completion callback rather than a
    // timer. Responses are emitted while the chunk is parsed and the
    // callback fires after that parse, so the window is exactly the
    // replay rather than a guess at how long one takes.
    //
    // The window can be long — the backlog is the whole 256 KiB ring —
    // and a user who types as the panel opens is not a terminal answer.
    // So only answers are dropped; keystrokes wait for the callback,
    // which sends them.
    if (hook._replaying) {
      if (!isTerminalReport(data)) {
        hook._pendingInput = [...hook._pendingInput, data];
      }
      return;
    }
    // The session's channel, not one captured here: the terminal outlives
    // a switch to another workspace, and so does this listener.
    hook._channel?.push("in", { b: data });
  });

  // One subscription covers every geometry change — divider drag, fullscreen
  // toggle and window resize all land as a box change on the panel element.
  term.onResize(({ cols, rows }: { cols: number; rows: number }) => {
    const payload = fitToResize(cols, rows);
    if (payload) sendResize(hook, payload);
  });

  // Fit on a frame boundary, never straight out of the observer.
  //
  // `fit.fit()` resizes the element the observer is watching, so calling
  // it synchronously in the callback re-enters the observer — the browser
  // reports "ResizeObserver loop completed with undelivered notifications"
  // and the panel visibly judders while a divider is being dragged.
  // Coalescing to one fit per animation frame also collapses the ~60
  // box changes a drag produces into one reflow each frame, which is what
  // makes the resize look like a terminal being resized rather than
  // content thrashing.
  //
  // rAF rather than a debounce timer: the frame callback IS the signal
  // that the browser is about to paint, so the fit lands on the same
  // frame as the layout that prompted it, and a drag stays live instead
  // of snapping after a trailing delay.
  const requestFit = (): void => {
    if (hook._fitFrame !== null) return;
    hook._fitFrame = requestAnimationFrame(() => {
      hook._fitFrame = null;
      fit.fit();
    });
  };

  const ro = new ResizeObserver(requestFit);
  ro.observe(hook.el);
  hook._resizeObserver = ro;

  // ThemeApply flips both attributes on <html> at runtime, from any tab.
  // `data-font` was missing here, so switching the mono face in Appearance
  // reskinned the whole app except this panel.
  const mo = new MutationObserver(() => {
    applyAppearance(hook);
  });
  mo.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme", "data-font"],
  });
  hook._themeObserver = mo;

  join(hook, term, fit, sessionId);
};

// The per-SESSION half of `attach`: one channel on the socket the hook
// already holds. Switching workspace runs only this half again — see
// `updated` — so the terminal, its GL context and the WebSocket survive.
const join = (
  hook: WorkspaceTerminalHook,
  term: Terminal,
  fit: FitAddon,
  sessionId: string,
): void => {
  const socket = hook._socket;
  if (!socket) return;
  hook._sessionId = sessionId;
  hook._lastGeometry = "";
  hook._lastSent = "";
  const channel = socket.channel(`terminal:${sessionId}`, {});
  hook._channel = channel;

  channel.on("out", (payload: unknown) => {
    term.write(bytesFromPush(payload));
  });

  channel
    .join()
    .receive("ok", (reply: unknown) => {
      // A late reply for a session the hook has switched away from.
      if (hook._channel !== channel) return;
      const backlog = (reply as { backlog_b64?: string } | null)?.backlog_b64;
      if (backlog) {
        // phoenix.js re-sends the join on every automatic rejoin — a
        // network blip, a deploy — and this reply carries the whole ring
        // again. Written onto a terminal that already shows it, the
        // history appears twice. The backlog is the complete picture, so
        // start from a blank screen.
        term.reset();
        hook._replaying = true;
        term.write(base64ToBytes(backlog), () => {
          // A callback from a replay this hook has since walked away from
          // must not end the replay that replaced it.
          if (hook._channel !== channel) return;
          hook._replaying = false;
          // What the user typed while the backlog was parsing goes out
          // now, in order, as one frame.
          const typed = hook._pendingInput;
          hook._pendingInput = [];
          if (typed.length > 0) channel.push("in", { b: typed.join("") });
        });
      }
      // The orchestrator replays the last geometry to the host when a
      // session reaches `ready`, but only if a browser ever reported one —
      // so the first report has to come from here or the PTY stays at the
      // kernel default 80x24.
      fit.fit();
      const payload = fitToResize(term.cols, term.rows);
      if (payload) sendResize(hook, payload);
    })
    .receive("error", (reply: unknown) => {
      if (hook._channel !== channel) return;
      const reason = (reply as { reason?: string } | null)?.reason ?? "error";
      term.writeln(`\x1b[31mworkspace terminal unavailable: ${reason}\x1b[0m`);
    });
};

// Fullscreen belongs to the PANEL, not to whatever session happens to be
// attached: the toggle renders as soon as the panel opens, before any
// workspace is picked. Wiring it inside `attach` left it dead in exactly
// that state.
const wireFullscreen = (hook: WorkspaceTerminalHook): void => {
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && isWorkspaceFullscreen()) {
      setWorkspaceFullscreen(false);
    }
  };
  document.addEventListener("keydown", onKey);
  hook._keyHandler = onKey;

  const fsButton = document.getElementById(
    validEditorElementId("workspaceFullscreenToggle"),
  );
  if (fsButton) {
    const onToggle = (): void => {
      setWorkspaceFullscreen(!isWorkspaceFullscreen());
    };
    fsButton.addEventListener("click", onToggle);
    hook._fsButton = fsButton;
    hook._fsHandler = onToggle;
  }
};

const unwireFullscreen = (hook: WorkspaceTerminalHook): void => {
  if (hook._keyHandler) {
    document.removeEventListener("keydown", hook._keyHandler);
    hook._keyHandler = null;
  }

  if (hook._fsButton && hook._fsHandler) {
    hook._fsButton.removeEventListener("click", hook._fsHandler);
  }
  hook._fsButton = null;
  hook._fsHandler = null;

  setWorkspaceFullscreen(false);
};

// Symmetric with `join`: everything that belongs to one SESSION.
const leave = (hook: WorkspaceTerminalHook): void => {
  // A queued resize would push onto a channel this hook no longer owns.
  if (hook._resizeTimer !== null) {
    clearTimeout(hook._resizeTimer);
    hook._resizeTimer = null;
  }
  hook._pendingResize = null;
  // The replay belongs to the channel being left; its callback no longer
  // runs its ending (see `join`), so end it here, and drop what was
  // typed for a session this hook is no longer showing.
  hook._replaying = false;
  hook._pendingInput = [];

  // Unbind before leaving: the server keeps sending until it processes the
  // leave, and the terminal those frames would land on now shows another
  // session.
  hook._channel?.off("out");
  hook._channel?.leave();
  hook._channel = null;
  hook._sessionId = null;
  hook._lastGeometry = "";
  hook._lastSent = "";
};

// Symmetric with `attach`, and every branch is unconditional: a listener or
// observer that outlives the hook keeps the whole Terminal alive.
const detach = (hook: WorkspaceTerminalHook): void => {
  hook._resizeObserver?.disconnect();
  hook._resizeObserver = null;
  // A frame already queued would call `fit.fit()` on a disposed addon.
  if (hook._fitFrame !== null) {
    cancelAnimationFrame(hook._fitFrame);
    hook._fitFrame = null;
  }

  hook._themeObserver?.disconnect();
  hook._themeObserver = null;

  leave(hook);

  if (hook._socket) {
    releaseLspSocket();
    hook._socket = null;
  }

  hook._term?.dispose();
  hook._term = null;
  hook._fit = null;
};

export const WorkspaceTerminal = {
  mounted(this: WorkspaceTerminalHook) {
    this._term = null;
    this._fit = null;
    this._channel = null;
    this._socket = null;
    this._sessionId = null;
    this._resizeObserver = null;
    this._fitFrame = null;
    this._resizeTimer = null;
    this._pendingResize = null;
    this._replaying = false;
    this._pendingInput = [];
    this._themeObserver = null;
    this._keyHandler = null;
    this._fsHandler = null;
    this._fsButton = null;
    this._lastGeometry = "";
    this._lastSent = "";
    wireFullscreen(this);
    attach(this);
  },

  // Picking a different workspace in the panel's dropdown re-renders the
  // element with a new `data-session-id`. The channel topic is the session
  // id, so the CHANNEL changes — and nothing else has to.
  //
  // Tearing the terminal down with it cost a WebGL context, a glyph atlas
  // and, whenever the terminal was the socket's only holder, a WebSocket
  // handshake with auth, all to change a topic. So a switch leaves the old
  // channel, blanks the screen and joins the new one on the socket already
  // held. Only the first workspace, or none, builds or disposes.
  updated(this: WorkspaceTerminalHook) {
    const next = this.el.dataset["sessionId"] ?? null;
    if (next === this._sessionId) return;
    const term = this._term;
    const fit = this._fit;
    if (next === null || term === null || fit === null) {
      detach(this);
      attach(this);
      return;
    }
    leave(this);
    term.reset();
    join(this, term, fit, next);
  },

  destroyed(this: WorkspaceTerminalHook) {
    detach(this);
    unwireFullscreen(this);
  },
};
